import React from 'react';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import { StringParam, useQueryParam } from 'use-query-params';
import { Card, CardContent, CardHeader, Grid, IconButton } from '@mui/material';
import { Edit, Add, Download, Visibility, VisibilityOff } from '@mui/icons-material';
import Date from 'components/formaters/Date';
import { CarModel } from 'models/StockModel';
import { CompanyType } from 'models/CompanyModel';
import useCarDocModals from 'modules/cars/hooks/useCarDocModals';
import useCars from 'modules/cars/hooks/useCars';
import { exportCertificateRequired, priceConfirmationRequired } from 'modules/cars/utils/common';
import auth from 'recoil/atoms/auth';


type PriceConfirmationCardProps = {
    car: CarModel,
}

const DocumentsCard = ({car}: PriceConfirmationCardProps) => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation('cars');
    const [docPreview, setDocPreview] = useQueryParam('doc', StringParam);
    const {
        openUploadPriceConfirmationModal,
        openUploadRepairEstimateModal,
        openUploadExplanatoryNoteModal,
        openUploadVehicleInvoiceModal,
        openUploadVehicleCatalogModal,
    } = useCarDocModals();
    const {downloadDocument} = useCars();
    const priceRequired = priceConfirmationRequired(car);

    if (!priceRequired && !car.damaged && exportCertificateRequired(car))
        return null;

    const canPreview = [CompanyType.Master, CompanyType.Shipper].includes(my.type);

    return (
        <Card sx={{mb: 2}}>
            <CardHeader
                title={t('documents')}
            />

            <CardContent sx={{fontSize: 'small'}}>
                <Grid container columnSpacing={2} alignItems="center">
                    {!exportCertificateRequired(car) && (
                        <>
                            <Grid item xs={5}>{t('vehicle_invoice')}:</Grid>

                            <Grid item xs={3}>
                                {car.documents.vehicleInvoice && <Date date={car.documents.vehicleInvoice}/>}
                            </Grid>

                            <Grid item xs={4} textAlign="right">
                                <IconButton
                                    aria-label="View Vehicle Invoice"
                                    onClick={() => openUploadVehicleInvoiceModal(car)}
                                >
                                    {car.documents.vehicleInvoice ? <Edit/> : <Add/>}
                                </IconButton>

                                {car.documents.vehicleInvoice && (
                                    <IconButton
                                        aria-label="Download Vehicle Invoice"
                                        onClick={() => downloadDocument(car.id, 'vehicle_invoice')}
                                    >
                                        <Download/>
                                    </IconButton>
                                )}
                            </Grid>

                            <Grid item xs={5}>{t('vehicle_catalog')}:</Grid>

                            <Grid item xs={3}>
                                {car.documents.vehicleCatalog && <Date date={car.documents.vehicleCatalog}/>}
                            </Grid>

                            <Grid item xs={4} textAlign="right">
                                <IconButton
                                    aria-label="Add Vehicle Catalog"
                                    onClick={() => openUploadVehicleCatalogModal(car)}
                                >
                                    <Add/>
                                </IconButton>

                                {car.documents.vehicleCatalog && (
                                    <IconButton
                                        aria-label="Download Vehicle Catalog"
                                        onClick={() => downloadDocument(car.id, 'vehicle_catalog')}
                                    >
                                        <Download/>
                                    </IconButton>
                                )}

                                {car.documents.vehicleCatalog && !isMobile && canPreview && (
                                    <IconButton
                                        aria-label="preview document"
                                        onClick={() => setDocPreview(docPreview === 'vehicle_catalog' ? undefined : 'vehicle_catalog')}
                                    >
                                        {docPreview === 'vehicle_catalog' ? <VisibilityOff/> : <Visibility/>}
                                    </IconButton>
                                )}
                            </Grid>
                        </>
                    )}

                    {priceRequired &&
                        <>
                            <Grid item xs={5}>{t('price_confirmation')}:</Grid>

                            <Grid item xs={3}>
                                {car.documents.priceConfirmation && <Date date={car.documents.priceConfirmation}/>}
                            </Grid>

                            <Grid item xs={4} textAlign="right">
                                <IconButton
                                    aria-label="Add Price Confirmation"
                                    onClick={() => openUploadPriceConfirmationModal(car)}
                                >
                                    <Add/>
                                </IconButton>

                                {car.documents.priceConfirmation && (
                                    <IconButton
                                        aria-label="Download Price Confirmation"
                                        onClick={() => downloadDocument(car.id, 'price_confirmation')}
                                    >
                                        <Download/>
                                    </IconButton>
                                )}

                                {car.documents.priceConfirmation && !isMobile && canPreview && (
                                    <IconButton
                                        aria-label="preview document"
                                        onClick={() => setDocPreview(docPreview === 'price_confirmation' ? undefined : 'price_confirmation')}
                                    >
                                        {docPreview === 'price_confirmation' ? <VisibilityOff/> : <Visibility/>}
                                    </IconButton>
                                )}
                            </Grid>

                            {!car.documents.priceConfirmation && (
                                <Grid item xs={12} sx={{color: 'warning.dark'}}>
                                    {t('invoice_required')}
                                </Grid>
                            )}
                        </>
                    }

                    {!!car.damaged && (
                        <>
                            <Grid item xs={5}>{t('explanatory_note')}:</Grid>

                            <Grid item xs={3}>
                                {car.documents.explanatoryNote && <Date date={car.documents.explanatoryNote}/>}
                            </Grid>

                            <Grid item xs={4} textAlign="right">
                                <IconButton
                                    aria-label="Add Explanatory Note"
                                    onClick={() => openUploadExplanatoryNoteModal(car)}
                                >
                                    <Add/>
                                </IconButton>

                                {car.documents.explanatoryNote && (
                                    <IconButton
                                        aria-label="Download Explanatory Note"
                                        onClick={() => downloadDocument(car.id, 'explanatory_note')}
                                    >
                                        <Download/>
                                    </IconButton>
                                )}

                                {car.documents.explanatoryNote && (
                                    <IconButton
                                        aria-label="preview document"
                                        onClick={() => setDocPreview(docPreview === 'explanatory_note' ? undefined : 'explanatory_note')}
                                    >
                                        {docPreview === 'explanatory_note' ? <VisibilityOff/> : <Visibility/>}
                                    </IconButton>
                                )}
                            </Grid>

                            <Grid item xs={5}>{t('repair_estimate')}:</Grid>

                            <Grid item xs={3}>
                                {car.documents.repairEstimate && <Date date={car.documents.repairEstimate}/>}
                            </Grid>

                            <Grid item xs={4} textAlign="right">
                                <IconButton
                                    aria-label="Add Repair Estimate"
                                    onClick={() => openUploadRepairEstimateModal(car)}
                                >
                                    <Add/>
                                </IconButton>

                                {car.documents.repairEstimate && (
                                    <IconButton
                                        aria-label="Download Repair Estimate"
                                        onClick={() => downloadDocument(car.id, 'repair_estimate')}
                                    >
                                        <Download/>
                                    </IconButton>
                                )}

                                {car.documents.repairEstimate && (
                                    <IconButton
                                        aria-label="preview document"
                                        onClick={() => setDocPreview(docPreview === 'repair_estimate' ? undefined : 'repair_estimate')}
                                    >
                                        {docPreview === 'repair_estimate' ? <VisibilityOff/> : <Visibility/>}
                                    </IconButton>
                                )}
                            </Grid>
                        </>
                    )}
                </Grid>
            </CardContent>
        </Card>
    );
};

export default DocumentsCard;
